import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Theme } from '../theme';

interface ShameEntry {
  id: number;
  name: string;
  x_handle: string;
  profile_image: string;
  price_prediction: string;
  predicted_price: number;
  target_time: string;
  source_url: string;
  quote: string;
  actual_price: number;
  pct_off: number;
  direction: 'too_high' | 'too_low';
}

interface ShamePageProps {
  isDarkMode: boolean;
  theme: Theme;
}

const flicker = keyframes`
  0%, 100% { opacity: 1; }
  92% { opacity: 1; }
  93% { opacity: 0.6; }
  94% { opacity: 1; }
  96% { opacity: 0.7; }
  97% { opacity: 1; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0a0000;
  color: #e0c0c0;
  padding: 40px 20px;
  font-family: 'Inter', sans-serif;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
  padding: 40px 20px;
  border-bottom: 2px solid #4a0000;
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: 900;
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, #8b0000 0%, #dc143c 50%, #8b0000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${flicker} 5s infinite;
  letter-spacing: -1px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 22px;
  color: #888;
  margin: 4px 0 12px 0;
  font-style: italic;
`;

const Tagline = styled.p`
  font-size: 15px;
  color: #5a3a3a;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 3px;
`;

const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  background: #1a0000;
  border: 1px solid #4a0000;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  color: #cc4444;
  text-align: center;
`;

const ShameCard = styled.div<{ missLevel: 'catastrophic' | 'massive' | 'moderate' }>`
  background: ${props =>
    props.missLevel === 'catastrophic' ? '#1a0000' :
    props.missLevel === 'massive' ? '#150800' : '#12100a'};
  border: 1px solid ${props =>
    props.missLevel === 'catastrophic' ? '#5a0000' :
    props.missLevel === 'massive' ? '#5a2a00' : '#3a3000'};
  border-left: 4px solid ${props =>
    props.missLevel === 'catastrophic' ? '#dc143c' :
    props.missLevel === 'massive' ? '#ff6600' : '#ccaa00'};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  transition: transform 0.15s ease, border-color 0.15s ease;

  &:hover {
    transform: translateX(4px);
    border-left-color: ${props =>
      props.missLevel === 'catastrophic' ? '#ff2255' :
      props.missLevel === 'massive' ? '#ff8833' : '#ffcc00'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 14px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

const RankBadge = styled.div`
  font-size: 28px;
  min-width: 44px;
  text-align: center;
  line-height: 1;
  padding-top: 4px;
`;

const Avatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #4a0000;
  flex-shrink: 0;
`;

const AvatarFallback = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #2a0000;
  border: 2px solid #4a0000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
`;

const InfluencerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const InfluencerName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #e0c0c0;
  margin-bottom: 2px;
`;

const Handle = styled.div`
  font-size: 13px;
  color: #664444;
`;

const QuoteText = styled.div`
  font-size: 13px;
  color: #886666;
  font-style: italic;
  margin-top: 6px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const PctBadge = styled.div<{ missLevel: 'catastrophic' | 'massive' | 'moderate' }>`
  font-size: 26px;
  font-weight: 900;
  color: ${props =>
    props.missLevel === 'catastrophic' ? '#dc143c' :
    props.missLevel === 'massive' ? '#ff6600' : '#ccaa00'};
  text-align: right;
  min-width: 100px;
  flex-shrink: 0;

  span {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: #664444;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  @media (max-width: 600px) {
    font-size: 20px;
    min-width: 80px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 12px;
`;

const MetaChip = styled.div`
  background: #0f0000;
  border: 1px solid #330000;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #886666;
`;

const DirectionBadge = styled.div<{ direction: 'too_high' | 'too_low' }>`
  background: ${props => props.direction === 'too_high' ? '#2a0a00' : '#001a2a'};
  border: 1px solid ${props => props.direction === 'too_high' ? '#8b2200' : '#003a5a'};
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  color: ${props => props.direction === 'too_high' ? '#ff6644' : '#44aaff'};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const SourceLink = styled.a`
  color: #554444;
  font-size: 12px;
  text-decoration: none;
  margin-left: auto;
  &:hover { color: #aa6666; }
`;

const LoadingMsg = styled.div`
  text-align: center;
  color: #554444;
  font-size: 18px;
  padding: 80px 0;
`;

const ErrorMsg = styled.div`
  text-align: center;
  color: #cc3333;
  font-size: 16px;
  padding: 40px;
`;

const PriceRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PriceItem = styled.div`
  font-size: 13px;
  color: #886666;

  strong {
    color: #cc9999;
    font-size: 14px;
  }
`;

function formatPrice(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `$${n.toFixed(2)}`;
}

function formatDate(s: string): string {
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
}

function getMissLevel(pct: number): 'catastrophic' | 'massive' | 'moderate' {
  if (pct > 100) return 'catastrophic';
  if (pct > 50) return 'massive';
  return 'moderate';
}

function getRankEmoji(rank: number): string {
  if (rank === 1) return '🤡';
  if (rank === 2) return '💩';
  if (rank === 3) return '😬';
  if (rank <= 5) return '🙈';
  if (rank <= 10) return '😭';
  return `#${rank}`;
}

const ShamePage: React.FC<ShamePageProps> = () => {
  const [entries, setEntries] = useState<ShameEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    axios.get('/api/shame')
      .then(res => {
        if (res.data.success) {
          setEntries(res.data.data);
        } else {
          setError('Failed to load data');
        }
      })
      .catch(err => {
        setError('Failed to load Hall of Shame data');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageContainer><LoadingMsg>🤡 Loading the shame...</LoadingMsg></PageContainer>;
  if (error) return <PageContainer><ErrorMsg>{error}</ErrorMsg></PageContainer>;

  const catastrophic = entries.filter(e => e.pct_off > 100).length;
  const avgPct = entries.length ? Math.round(entries.reduce((s, e) => s + e.pct_off, 0) / entries.length) : 0;
  const worstPct = entries.length ? entries[0].pct_off : 0;

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>🤡 Hall of Shame</Title>
          <Subtitle>Aged Like Milk</Subtitle>
          <Tagline>The Oracle Was... Not</Tagline>
        </Header>

        <StatsBar>
          <StatChip>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#dc143c' }}>{entries.length}</div>
            <div>Shameful Predictions</div>
          </StatChip>
          <StatChip>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#ff6600' }}>{catastrophic}</div>
            <div>Catastrophic Misses (&gt;100%)</div>
          </StatChip>
          <StatChip>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#ccaa00' }}>{avgPct}%</div>
            <div>Avg % Off</div>
          </StatChip>
          <StatChip>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#dc143c' }}>{worstPct.toFixed(0)}%</div>
            <div>Worst Miss</div>
          </StatChip>
        </StatsBar>

        {entries.map((entry, index) => {
          const rank = index + 1;
          const level = getMissLevel(entry.pct_off);
          return (
            <ShameCard key={entry.id} missLevel={level}>
              <CardHeader>
                <RankBadge>{getRankEmoji(rank)}</RankBadge>
                {entry.profile_image ? (
                  <Avatar src={entry.profile_image} alt={entry.name} onError={e => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }} />
                ) : (
                  <AvatarFallback>👤</AvatarFallback>
                )}
                <InfluencerInfo>
                  <InfluencerName>{entry.name}</InfluencerName>
                  {entry.x_handle && <Handle>@{entry.x_handle}</Handle>}
                  {entry.quote && (
                    <QuoteText>"{entry.quote}"</QuoteText>
                  )}
                </InfluencerInfo>
                <PctBadge missLevel={level}>
                  {entry.pct_off >= 1000
                    ? `${(entry.pct_off / 1000).toFixed(1)}K%`
                    : `${entry.pct_off.toFixed(0)}%`}
                  <span>off</span>
                </PctBadge>
              </CardHeader>

              <PriceRow>
                <PriceItem>
                  Predicted: <strong>{formatPrice(entry.predicted_price)}</strong>
                  <span style={{ fontSize: 11, color: '#554444', marginLeft: 6 }}>({entry.price_prediction})</span>
                </PriceItem>
                <PriceItem>
                  Actual BTC: <strong>{formatPrice(entry.actual_price)}</strong>
                </PriceItem>
              </PriceRow>

              <MetaRow>
                <DirectionBadge direction={entry.direction}>
                  {entry.direction === 'too_high' ? '↑ Too High' : '↓ Too Low'}
                </DirectionBadge>
                <MetaChip>📅 {formatDate(entry.target_time)}</MetaChip>
                {entry.source_url && (
                  <SourceLink href={entry.source_url} target="_blank" rel="noopener noreferrer">
                    View Source →
                  </SourceLink>
                )}
              </MetaRow>
            </ShameCard>
          );
        })}

        {entries.length === 0 && (
          <LoadingMsg>No shameful predictions found. Everyone was right? Impossible.</LoadingMsg>
        )}

        <div style={{ textAlign: 'center', padding: '40px 0 20px', color: '#332222', fontSize: 13 }}>
          The records are permanent. The embarrassment is forever.
        </div>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ShamePage;
